import { useApiUrl, useCustom } from "@refinedev/core";
import { Card, Descriptions, Typography, Space, Tag, Skeleton } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import { UserOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";

export function PayoutShow() {
  const { Title, Text } = Typography;
  const { id } = useParams();
  const navigate = useNavigate();
  const apiUrl = useApiUrl();

  // State for payout data
  const [payout, setPayout] = useState<any>(null);
  const [payoutLoading, setPayoutLoading] = useState(true);
  const [payoutError, setPayoutError] = useState<string | null>(null);

  // Load basic payout information
  useEffect(() => {
    const loadPayout = async () => {
      try {
        setPayoutLoading(true);
        const response = await fetch(`${apiUrl}/payout/show/${id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem('refine-auth')}`,
          },
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error(`Failed to load payout: ${response.statusText}`);
        }

        const result = await response.json();
        const payoutData = result.payout;

        if (payoutData) {
          setPayout(payoutData);
        } else {
          console.warn('No payout data received from API');
        }
      } catch (error: any) {
        setPayoutError(error.message || 'Failed to load payout information');
        console.error('Error loading payout:', error);
      } finally {
        setPayoutLoading(false);
      }
    };

    if (id) {
      loadPayout();
    }
  }, [id, apiUrl]);

  return (
    <div style={{ padding: 16 }}>
      <Title level={3} style={{ marginBottom: 16 }}>
        Payout Details
      </Title>

      {/* Payout Loading State */}
      {payoutLoading && (
        <Card style={{ marginBottom: 16 }}>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      )}

      {/* Payout Error State */}
      {payoutError && (
        <Card style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%', textAlign: 'center' }}>
            <Text type="danger">{payoutError}</Text>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '8px 16px',
                background: '#1890ff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </Space>
        </Card>
      )}

      {/* Payout Details */}
      {payout && !payoutLoading && !payoutError && (
        <Card style={{ marginBottom: 16 }}>
          <Descriptions bordered column={{ xs: 1, sm: 2, md: 2 }}>
            <Descriptions.Item label="Payout ID">
              <Text copyable={{ text: payout.id }}>{payout.id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Instructor">
              <a onClick={() => navigate(`/users/${payout.instructorID}`)}>
                <Space>
                  <UserOutlined />
                  {payout.instructorProfile?.fullName ||
                   payout.instructorProfile?.email ||
                   payout.instructorID}
                </Space>
              </a>
            </Descriptions.Item>
            <Descriptions.Item label="Amount">
              <Text strong style={{ fontSize: '18px', color: '#52c41a' }}>${payout.amount}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={
                payout.status === 'PAID' ? 'green' :
                payout.status === 'PENDING' ? 'orange' :
                payout.status === 'FAILED' ? 'red' :
                payout.status === 'PROCESSING' ? 'blue' : 'default'
              }>
                {payout.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Created">
              <Text>{payout.createdAt ? new Date(payout.createdAt).toLocaleString() : '-'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Updated">
              <Text>{payout.updatedAt ? new Date(payout.updatedAt).toLocaleString() : '-'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Stripe Payout ID" span={2}>
              {payout.stripePayoutID ? (
                <Text copyable={{ text: payout.stripePayoutID }}>
                  {payout.stripePayoutID}
                </Text>
              ) : (
                <Text type="secondary">Not available</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Failure Reason" span={2}>
              {payout.failureReason || <Text type="secondary">No issues</Text>}
            </Descriptions.Item>
            {payout.metadata && (
              <Descriptions.Item label="Metadata" span={2}>
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: '8px', 
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflow: 'auto',
                  fontSize: '12px'
                }}>
                  {JSON.stringify(payout.metadata, null, 2)}
                </pre>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )}
    </div>
  );
}